import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Mail, CheckCheck, Timer, Users, Trophy, Repeat, Percent, Inbox as InboxIcon } from "lucide-react";
import { api } from "../../lib/api";

const PERIOD_KEYS = ["7d", "30d", "3m", "1y"];
const CARD_ICONS = [Mail, CheckCheck, Timer, Users, Trophy, Repeat, Percent, InboxIcon];
const CARD_KEYS = ["received", "processed", "avgResponse", "leads", "converted", "followups", "responseRate", "pending"];

export default function Analytics() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);
  const periodLabels = t("analytics.periods");

  useEffect(() => {
    setData(null);
    api.get("/analytics", { params: { period } }).then((r) => setData(r.data)).catch(() => {});
  }, [period]);

  const totals = data?.totals || {};
  const values = [
    totals.emails_received, totals.emails_processed,
    totals.avg_response_minutes != null ? `${totals.avg_response_minutes} min` : "—",
    totals.leads_detected, totals.leads_converted, totals.followups_sent,
    totals.followup_response_rate != null ? `${totals.followup_response_rate} %` : "—",
    totals.emails_pending,
  ];

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl tracking-tighter">{t("analytics.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{t("analytics.subtitle")}</p>
        </div>
        <div className="flex gap-2" data-testid="analytics-periods">
          {PERIOD_KEYS.map((k, i) => (
            <button key={k} onClick={() => setPeriod(k)} data-testid={`period-${k}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${period === k ? "bg-primary text-primary-foreground border-primary dark:bg-electric dark:border-electric" : "border-border bg-card hover:border-electric/50"}`}>
              {periodLabels[i]}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(8)].map((_, i) => <div key={i} className="skeleton-shimmer h-24 rounded-2xl" />)}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-cards">
            {CARD_KEYS.map((key, i) => {
              const Icon = CARD_ICONS[i];
              return (
              <motion.div key={key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="rounded-2xl border border-border bg-card p-5" data-testid={`analytics-card-${key}`}>
                <Icon size={16} className="text-electric mb-3" />
                <p className="font-display font-black text-2xl tracking-tighter">{values[i] ?? "—"}</p>
                <p className="text-[11px] text-muted-foreground mt-1">{t(`analytics.cards.${key}`)}</p>
              </motion.div>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="chart-emails">
              <h2 className="font-display font-bold text-sm mb-6">{t("analytics.chart1")}</h2>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={data.series} margin={{ left: -20, right: 6 }}>
                  <defs>
                    <linearGradient id="gRec" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0047FF" stopOpacity={0.28} />
                      <stop offset="100%" stopColor="#0047FF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gPro" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.25} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                  <Area type="monotone" dataKey="emails_received" name={t("analytics.received")} stroke="#0047FF" strokeWidth={2} fill="url(#gRec)" />
                  <Area type="monotone" dataKey="emails_processed" name={t("analytics.processed2")} stroke="#10B981" strokeWidth={2} fill="url(#gPro)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6" data-testid="chart-leads">
              <h2 className="font-display font-bold text-sm mb-6">{t("analytics.chart2")}</h2>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={data.series} margin={{ left: -20, right: 6 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(d) => d.slice(5)} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(var(--border))", background: "hsl(var(--card))", fontSize: 12 }} />
                  <Bar dataKey="leads_detected" name={t("analytics.leadsName")} fill="#0047FF" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="followups_sent" name={t("analytics.followupsName")} fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
